/**
 * Who the signed-in admin is, for the Admin Hub only.
 *
 * `super_admin` unlocks the governed actions: 100% free coupons, permanent
 * deletion and canonical price change requests.
 */

import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface AdminRoleState {
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export function useAdminRole(): AdminRoleState {
  const { user } = useAuth();
  const [state, setState] = useState<AdminRoleState>({
    loading: true,
    isAdmin: false,
    isSuperAdmin: false,
  });

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setState({ loading: false, isAdmin: false, isSuperAdmin: false });
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (cancelled) return;
      const roles = (data ?? []).map((r) => r.role as string);
      setState({
        loading: false,
        isSuperAdmin: roles.includes("super_admin"),
        isAdmin: roles.includes("admin") || roles.includes("super_admin"),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return state;
}
